import { Ref, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { CSSTransition } from "react-transition-group";
import {
  DifferentAddressInput,
  DifferentAddressSchema,
  TransportInput,
  TransportSchema,
} from "../validation/transport";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, Controller } from "react-hook-form";
import { useGetCountries } from "@/services/RegisterCompany";
import { useGetCompanyAddresses, useGetCompanyByIdPublic } from "@/services/Company";
import { findDeliveryAddress } from "@/utils/AddressHelper";
import { CreateContractWizard } from "../helpers/helpers";

import { Grid } from "@mantine/core";
import Checkbox from "@/components/atoms/Checkbox";
import CTA from "@/components/atoms/CTA";
import RadioButtonGroup from "@/components/molecules/RadioButtonGroup";
import Text from "@/components/atoms/Text";
import Divider from "@/components/atoms/Divider";
import Stack from "@/components/atoms/Stack";
import TransportResponsibleEnum from "@/constants/enums/TransportResponsibleEnum";
import { useAuth } from "@/contexts/AuthContext";
import AddressTypeEnum from "@/constants/enums/AddressTypeEnum";
import ContractTradeTypeEnum from "@/constants/enums/ContractTradeTypeEnum";
import WeightResponsibleEnum from "@/constants/enums/WeightResponsibleEnu";
import TextInput from "@/components/atoms/TextInput";
import SelectCountry from "@/components/molecules/SelectInputs/SelectCountry";

interface Props {
  onValidationSuccess: (data: any) => void;
  onChangeWizardData: (changed: any) => void;
  wizardData: CreateContractWizard;
  ref?: Ref<HTMLFormElement>;
}

function Transport({ onValidationSuccess, onChangeWizardData, wizardData, ref }: Props) {
  const { t } = useTranslation();
  const { currentCompany, user } = useAuth();

  const differentAddressNodeRef = useRef(null);
  const [differentFromIsChecked, setIsDifferentFromChecked] = useState<boolean>(false);
  const [differentToIsChecked, setIsDifferentToChecked] = useState<boolean>(false);

  const { data: countryOptions } = useGetCountries(user?.language.id as number, {
    enabled: !!user?.language.id,
  });
  const { data: companyAddresses } = useGetCompanyAddresses(currentCompany?.companyId as number, {
    enabled: !!currentCompany?.companyId,
  });
  const { data: companyAddressesTradingPartner } = useGetCompanyAddresses(wizardData.tradingPartner);
  const { data: tradingPartnerCompany } = useGetCompanyByIdPublic(wizardData.tradingPartner);

  const transportResponsibleOptions = [
    { value: TransportResponsibleEnum.REVIVER, label: "Reviver" },
    { value: TransportResponsibleEnum.BUYER, label: t("orders:buyer") },
    { value: TransportResponsibleEnum.SELLER, label: t("orders:seller") },
  ];
  const weightReponsibleOptions = [
    { value: WeightResponsibleEnum.BUYER, label: t("orders:buyer") },
    { value: WeightResponsibleEnum.SELLER, label: t("orders:seller") },
  ];

  const { handleSubmit, control } = useForm<TransportInput>({
    resolver: zodResolver(TransportSchema),
    defaultValues: {
      transportResponsible: wizardData.transportResponsible,
      weightResponsible: wizardData.weightResponsible,
    },
  });

  const onSubmit = (data: any) => {
    console.log("Transport onSubmit", data);
    onChangeWizardData(data);
    onValidationSuccess(data);
  };

  const {
    handleSubmit: handleSubmitAddress,
    control: controlAddress,
    reset: resetAddress,
  } = useForm<DifferentAddressInput>({
    resolver: zodResolver(DifferentAddressSchema),
    reValidateMode: "onChange",
    defaultValues: {
      address: "",
      countryId: 578,
      postCode: "",
      place: "",
      addressType: AddressTypeEnum.SHIPPING,
    },
  });

  const onSubmitAddress = (data: any) => {
    console.log("onSubmitAddress", data);
    if (differentFromIsChecked) {
      onChangeWizardData({ transportFromAddress: data });
      setIsDifferentFromChecked(false);
      resetAddress();
    } else {
      onChangeWizardData({ transportToAddress: data });
      setIsDifferentToChecked(false);
      resetAddress();
    }
  };

  function deliverToAddress() {
    if (wizardData.transportToAddress) {
      return `${wizardData.transportToAddress.address}, ${wizardData.transportToAddress.postCode} ${wizardData.transportToAddress.place}, ${
        countryOptions?.find((country) => country.id === wizardData.transportToAddress?.countryId)?.name
      }`;
    }
    if (!companyAddresses || !companyAddressesTradingPartner) return;
    if (wizardData.contractType === ContractTradeTypeEnum.BUY) {
      return findDeliveryAddress(companyAddresses);
    } else {
      return findDeliveryAddress(companyAddressesTradingPartner);
    }
  }

  function deliverFromAddress() {
    if (wizardData.transportFromAddress) {
      return `${wizardData.transportFromAddress.address}, ${wizardData.transportFromAddress.postCode} ${wizardData.transportFromAddress.place}, ${
        countryOptions?.find((country) => country.id === wizardData.transportFromAddress?.countryId)?.name
      }`;
    }
    if (!companyAddresses || !companyAddressesTradingPartner) return;
    if (wizardData.contractType === ContractTradeTypeEnum.BUY) {
      return findDeliveryAddress(companyAddressesTradingPartner);
    } else {
      return findDeliveryAddress(companyAddresses);
    }
  }

  return (
    <>
      <form ref={ref} onSubmit={handleSubmit(onSubmit)} data-testid={"contract-transport-form"}>
        <Grid>
          <Grid.Col span={{ base: 12, lg: 6 }}>
            <Stack gap="15px">
              <Text size="sm" color="secondary">
                {t("contracts:transportResponsible")}
              </Text>
              <Controller
                control={control}
                name="transportResponsible"
                render={({ field: { onChange, value } }) => (
                  <RadioButtonGroup
                    testId="transportResponsible"
                    name="transportResponsible"
                    options={transportResponsibleOptions}
                    defaultValue={value}
                    onValueChange={(value: string) => {
                      onChange(value);
                    }}
                  />
                )}
              />
            </Stack>
          </Grid.Col>
          <Grid.Col span={{ base: 12, lg: 6 }}>
            <Stack gap="15px">
              <Text size="sm" color="secondary">
                {t("contracts:weightResponsible")}
              </Text>
              <Controller
                control={control}
                name="weightResponsible"
                render={({ field: { onChange, value } }) => (
                  <RadioButtonGroup
                    testId="weightResponsible"
                    name="weightResponsible"
                    options={weightReponsibleOptions}
                    defaultValue={value}
                    onValueChange={(value: string) => {
                      onChange(value);
                    }}
                  />
                )}
              />
            </Stack>
          </Grid.Col>
        </Grid>
      </form>
      <Grid>
        <Grid.Col span={12}>
          <Divider />
        </Grid.Col>

        <Grid.Col span={{ base: 12, lg: 6 }}>
          <Stack gap="20px">
            <Text size="sm" color="secondary" data-testid={"seller-address"}>
              {`${t("contracts:sellersAddress")} (${wizardData.contractType === ContractTradeTypeEnum.BUY ? tradingPartnerCompany?.companyName : currentCompany?.companyName})`}
            </Text>
            <Text>{deliverFromAddress()}</Text>
            <Checkbox
              data-testid="different-from-checkbox"
              label={t("actions:otherAddress")}
              checked={differentFromIsChecked}
              disabled={differentToIsChecked}
              onChange={() => setIsDifferentFromChecked(!differentFromIsChecked)}
            />
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ base: 12, lg: 6 }}>
          <Stack gap="20px">
            <Text size="sm" color="secondary" data-testid={"buyer-address"}>
              {`${t("contracts:buyersAddress")} (${wizardData.contractType === ContractTradeTypeEnum.BUY ? currentCompany?.companyName : tradingPartnerCompany?.companyName})`}
            </Text>
            <Text>{deliverToAddress()}</Text>
            <Checkbox
              data-testid="different-to-checkbox"
              label={t("actions:otherAddress")}
              checked={differentToIsChecked}
              disabled={differentFromIsChecked}
              onChange={() => setIsDifferentToChecked(!differentToIsChecked)}
            />
          </Stack>
        </Grid.Col>
      </Grid>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmitAddress(onSubmitAddress)(e);
        }}
        ref={differentAddressNodeRef}
      >
        <Grid>
          <CSSTransition
            in={differentFromIsChecked || differentToIsChecked}
            timeout={{
              enter: 1000,
              exit: 200,
            }}
            mountOnEnter
            unmountOnExit
            classNames="create-order-transitions"
            nodeRef={differentAddressNodeRef}
          >
            <>
              <Grid.Col span={{ base: 12, lg: 6 }}>
                <Controller
                  control={controlAddress}
                  name="address"
                  render={({ field, fieldState }) => (
                    <TextInput
                      id="create-order-address"
                      data-testid="create-order-address"
                      label={t("orders:orderAddress")}
                      error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                      {...field}
                    />
                  )}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, lg: 6 }}>
                <Controller
                  control={controlAddress}
                  name="countryId"
                  render={({ field: { onChange, value } }) => (
                    <SelectCountry
                      label={t("common:country")}
                      data-testid="create-order-country"
                      valueKey={"id"}
                      labelKey={"name"}
                      onChange={(value) => onChange(value ? Number(value) : null)}
                      value={value.toString()}
                    />
                  )}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, lg: 6 }}>
                <Controller
                  control={controlAddress}
                  name="postCode"
                  render={({ field, fieldState }) => (
                    <TextInput
                      id="create-order-postCode"
                      data-testid="create-order-postCode"
                      label={t("orders:postCode")}
                      error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                      {...field}
                    />
                  )}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, lg: 6 }}>
                <Controller
                  control={controlAddress}
                  name="place"
                  render={({ field, fieldState }) => (
                    <TextInput
                      id="create-order-place"
                      data-testid="create-order-place"
                      label={t("orders:place")}
                      error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                      {...field}
                    />
                  )}
                />
                <CTA
                  mt={10}
                  id="checkAddress"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSubmitAddress(onSubmitAddress)(e);
                  }}
                  data-testid="submit-different-address"
                >
                  {differentFromIsChecked ? t("actions:saveNewFromAddress") : t("actions:saveNewToAddress")}
                </CTA>
              </Grid.Col>
            </>
          </CSSTransition>
        </Grid>
      </form>
    </>
  );
}

export default Transport;
